import { FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Alert, Button } from '@mui/material';
import {
  IconChevronLeft,
  IconShip,
  IconMapPin,
  IconPlane,
  IconAnchor,
  IconUsers,
  IconScubaMask,
} from '@tabler/icons-react';
import useGetPartnerBookings from '../../../hooks/useGetPartnerBookings';
import { getErrorMessage } from '../../../utils/errorUtils';
import { format } from 'date-fns';
import BookingStatusBadge from './components/BookingStatusBadge';

const BookingDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    bookings,
    isLoading,
    isError,
    error: errorDetails,
  } = useGetPartnerBookings();

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="error" className="mb-4">
          <div className="font-semibold">Error loading booking</div>
          <div className="mt-2 text-sm">{getErrorMessage(errorDetails)}</div>
        </Alert>
      </div>
    );
  }

  const booking = bookings.find(b => b.bookingId === parseInt(id || '0', 10));

  if (!booking) {
    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="warning" className="mb-4">
          Booking not found
        </Alert>
        <Button
          startIcon={<IconChevronLeft />}
          onClick={() => navigate('/partner/bookings')}
        >
          Back to Bookings
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6">
        <Button
          startIcon={<IconChevronLeft />}
          onClick={() => navigate('/partner/bookings')}
          className="mb-4"
        >
          Back to Bookings
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Booking #{booking.bookingId}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              View booking details and guest information
            </p>
          </div>
          <BookingStatusBadge status={booking.bookingStatus} />
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <IconShip className="h-5 w-5" />
            Trip Details
          </h2>
          <dl className="mt-4 space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Boat</dt>
              <dd className="mt-1 text-sm text-gray-900">{booking.boatName}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Route</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {booking.routeName || 'N/A'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Embarkation Date
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {format(new Date(booking.embarkationDate), 'MMM dd, yyyy')}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Disembarkation Date
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {format(new Date(booking.disembarkationDate), 'MMM dd, yyyy')}
              </dd>
            </div>
            {booking.diveCount && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
                  <IconScubaMask className="h-4 w-4" />
                  Number of Dives
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {booking.diveCount}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <IconUsers className="h-5 w-5" />
            Customer Profile
          </h2>
          <dl className="mt-4 space-y-3">
            {booking.leadGuestFullName ? (
              <>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Lead Guest Name
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900">
                    {booking.leadGuestFullName}
                  </dd>
                </div>
                {booking.leadGuestEmail && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {booking.leadGuestEmail}
                    </dd>
                  </div>
                )}
                {booking.leadGuestPhone && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {booking.leadGuestPhone}
                    </dd>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-gray-500">
                Customer information not available
              </div>
            )}
            <div className="mt-4 border-t border-gray-200 pt-3">
              <dt className="text-sm font-medium text-gray-500">
                Number of Guests
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {booking.guestCount}
              </dd>
            </div>
            {booking.isLeadGuest !== undefined && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Lead Guest
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {booking.isLeadGuest ? 'Yes' : 'No'}
                </dd>
              </div>
            )}
            {booking.guestNumber && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Guest Number
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {booking.guestNumber}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {(booking.departurePier || booking.returnPier) && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <IconAnchor className="h-5 w-5" />
              Location & Pier Information
            </h2>
            <dl className="mt-4 space-y-3">
              {booking.departurePier && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Departure Pier
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {booking.departurePier}
                  </dd>
                </div>
              )}
              {booking.returnPier && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Return Pier
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {booking.returnPier}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {(booking.arrivalAirport ||
          booking.departureAirport ||
          booking.arrivalTime ||
          booking.departureTime) && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <IconPlane className="h-5 w-5" />
              Airport Transfer Information
            </h2>
            <dl className="mt-4 space-y-3">
              {booking.arrivalAirport && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Arrival Airport
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {booking.arrivalAirport}
                  </dd>
                </div>
              )}
              {booking.arrivalTime && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Arrival Time
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {booking.arrivalTime}
                  </dd>
                </div>
              )}
              {booking.departureAirport && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Departure Airport
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {booking.departureAirport}
                  </dd>
                </div>
              )}
              {booking.departureTime && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Departure Time
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {booking.departureTime}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <IconMapPin className="h-5 w-5" />
            Additional Information
          </h2>
          <dl className="mt-4 space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Booking ID</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {booking.bookingId}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Trip ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{booking.tripId}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Boat ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{booking.boatId}</dd>
            </div>
            {booking.boatSlug && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Boat Slug</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {booking.boatSlug}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default BookingDetail;
